import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { Drawer } from "@/shared/ui/drawer";

import { useDiffHistory } from "../api/use-diff-history";
import { formatPct } from "../lib/diff-format";

import { Sparkline } from "./Sparkline";

interface DiffHistoryDrawerProps {
  /** null keeps the drawer closed and the query idle. */
  sku: string | null;
  onClose: () => void;
}

/**
 * The SKU's last 30 shelf-count reports (spec D6). The sparkline is built from
 * these same rows — one point per REPORT, not per day — so it never disagrees
 * with the table underneath it.
 *
 * Returns null when closed. Drawer keeps its panel mounted-but-hidden with a
 * `hidden` class, which jsdom does not apply (Tailwind is not compiled in
 * tests), so leaving the content mounted would make a closed drawer still
 * queryable. Unmounting is both simpler and honest.
 */
export function DiffHistoryDrawer({ sku, onClose }: DiffHistoryDrawerProps): JSX.Element | null {
  const { t, i18n } = useTranslation("inventory_diff");
  const query = useDiffHistory(sku);
  const nf = new Intl.NumberFormat(i18n.language);
  const items = query.data?.items ?? [];
  if (sku === null) return null;

  // Oldest → newest, so the sparkline reads left-to-right like a timeline.
  const trend = items.map((r) => Math.abs(r.diff)).reverse();
  const anyOver = items.some((r) => r.above_threshold);

  return (
    <Drawer
      open
      onClose={onClose}
      title={t("drawer.title", { sku })}
      subtitle={t("drawer.subtitle", { count: items.length })}
    >
      <div data-testid="diff-drawer" className="px-5 py-4">
        {query.isError ? (
          <p className="py-10 text-center text-sm">{t("drawer.error")}</p>
        ) : query.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-9 animate-pulse rounded-[10px] bg-[var(--surface-sunken)]"
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="py-10 text-center text-sm">{t("drawer.empty")}</p>
        ) : (
          <>
            <div className="mb-4">
              <Sparkline trend={trend} over={anyOver} testId="diff-history-spark" />
            </div>
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="border-b border-[var(--row-divider)]">
                  <th className="py-2 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
                    {t("drawer.reported_at")}
                  </th>
                  <th className="py-2 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
                    {t("column.expect")}
                  </th>
                  <th className="py-2 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
                    {t("column.real")}
                  </th>
                  <th className="py-2 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
                    {t("column.diff")}
                  </th>
                  <th className="py-2 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
                    {t("column.pct")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--row-divider)]">
                {items.map((r) => (
                  <tr
                    key={r.run_id}
                    data-testid={`diff-history-row-${r.run_id}`}
                    data-over={r.above_threshold ? "true" : "false"}
                    className={r.above_threshold ? "bg-[var(--intent-danger-bg)]" : undefined}
                  >
                    <td className="py-2 text-[13px] text-[var(--text-secondary)]">
                      {new Date(r.reported_at).toLocaleString(i18n.language, {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="sku py-2 text-right text-[13px]">
                      {nf.format(r.expect_quantity)}
                    </td>
                    <td className="sku py-2 text-right text-[13px]">
                      {nf.format(r.real_quantity)}
                    </td>
                    <td
                      className={`sku py-2 text-right text-[13px] font-semibold ${r.above_threshold ? "text-[var(--intent-danger-fg)]" : ""}`}
                    >
                      {r.diff > 0 ? `+${nf.format(r.diff)}` : nf.format(r.diff)}
                    </td>
                    <td className="py-2 text-right text-[13px] text-[var(--text-secondary)]">
                      {formatPct(r.pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Drawer>
  );
}
